/**
 * Duplicates a MAUI app by calling the Remix duplicate API endpoint.
 *
 * @param appId - The ID of the MAUI app to duplicate
 * @param containerContentId - The Confluence page or blog post ID that will contain the duplicated app
 * @param cloudId - Optional cloud ID used to construct the container ARI
 * @param contentType - Optional content type of the container ('page' or 'blogpost'). Defaults to 'page'.
 * @returns The ID of the newly duplicated app
 */
export async function duplicateMauiApp(
	appId: string,
	containerContentId: string,
	cloudId?: string,
	contentType?: string,
): Promise<{ id: string }> {
	const ariContentType = contentType === 'blogpost' ? 'blogpost' : 'page';
	const containerAri = cloudId
		? `ari:cloud:confluence:${cloudId}:${ariContentType}/${containerContentId}`
		: undefined;

	const response = await fetch(
		`/gateway/api/remix/api/v1/ui/${encodeURIComponent(appId)}/duplicate`,
		{
			method: 'POST',
			credentials: 'include',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				containerAri: containerAri ?? containerContentId,
			}),
		},
	);

	if (!response.ok) {
		throw new Error("We couldn't duplicate this component. Please try again.");
	}

	const data = await response.json();
	if (!data?.id) {
		throw new Error(
			"We couldn't duplicate this component. The server did not return a new component ID.",
		);
	}

	return { id: data.id };
}
